import { api } from '@/shared/services/api';
import {
  type UserTemplate,
  type SystemTemplate,
  type ResolvedTemplate,
  type CreateTemplateDTO,
  type UpdateTemplateDTO,
  userTemplateSchema,
  systemTemplateSchema,
  resolvedTemplateSchema,
  userTemplateListResponseSchema,
  systemTemplateListResponseSchema,
  type UserTemplateListResponse,
  type SystemTemplateListResponse,
} from './types';

// ── User Templates ────────────────────────────────────────────────────────────

export interface ListUserTemplatesParams {
  tool_target?: string;
  active_only?: boolean;
  page?:        number;
  limit?:       number;
}

export const templateApi = {
  // List user's own templates
  listUserTemplates: async (params?: ListUserTemplatesParams): Promise<UserTemplateListResponse> => {
    const searchParams: Record<string, string> = {};
    if (params?.tool_target) searchParams.tool_target = params.tool_target;
    if (params?.active_only) searchParams.active_only = 'true';
    if (params?.page)        searchParams.page = String(params.page);
    if (params?.limit)       searchParams.limit = String(params.limit);

    const raw = await api.get('user/templates', { searchParams }).json();
    return userTemplateListResponseSchema.parse(raw);
  },

  // Get single template
  getTemplate: async (id: string): Promise<UserTemplate> => {
    const raw = await api.get(`user/templates/${id}`).json();
    return userTemplateSchema.parse(raw);
  },

  // Resolve: get best template for a given tool (server-side resolution)
  resolveTemplate: async (toolName: string): Promise<ResolvedTemplate> => {
    const raw = await api.get('user/templates/resolve', {
      searchParams: { tool: toolName },
    }).json();
    return resolvedTemplateSchema.parse(raw);
  },

  // Create
  createTemplate: async (dto: CreateTemplateDTO): Promise<UserTemplate> => {
    const raw = await api.post('user/templates', { json: dto }).json();
    return userTemplateSchema.parse(raw);
  },

  // Update (full replace)
  updateTemplate: async (id: string, dto: UpdateTemplateDTO): Promise<UserTemplate> => {
    const raw = await api.put(`user/templates/${id}`, { json: dto }).json();
    return userTemplateSchema.parse(raw);
  },

  // Partial update (PATCH)
  patchTemplate: async (
    id: string,
    patch: { is_active?: boolean; is_default?: boolean }
  ): Promise<UserTemplate> => {
    const raw = await api.patch(`user/templates/${id}`, { json: patch }).json();
    return userTemplateSchema.parse(raw);
  },

  // Delete
  deleteTemplate: async (id: string): Promise<void> => {
    await api.delete(`user/templates/${id}`).json<void>();
  },

  // Duplicate
  duplicateTemplate: async (id: string): Promise<UserTemplate> => {
    const raw = await api.post(`user/templates/${id}/duplicate`).json();
    return userTemplateSchema.parse(raw);
  },

  // Set as default for its tool
  setDefaultTemplate: async (id: string): Promise<UserTemplate> => {
    const raw = await api.post(`user/templates/${id}/set-default`).json();
    return userTemplateSchema.parse(raw);
  },

  // ── System Templates (read-only) ──────────────────────────────────────────

  listSystemTemplates: async (): Promise<SystemTemplateListResponse> => {
    const raw = await api.get('system/templates').json();
    return systemTemplateListResponseSchema.parse(raw);
  },

  getSystemTemplate: async (toolTarget: string): Promise<SystemTemplate> => {
    const raw = await api.get(`system/templates/${encodeURIComponent(toolTarget)}`).json();
    return systemTemplateSchema.parse(raw);
  },

  // ── Enterprise Templates ───────────────────────────────────────────────

  listEnterpriseTemplates: async (params?: { tool_target?: string; active_only?: boolean; limit?: number }) => {
    const searchParams: Record<string, string> = {};
    if (params?.tool_target) searchParams.tool_target = params.tool_target;
    if (params?.active_only) searchParams.active_only = 'true';
    if (params?.limit)       searchParams.limit = String(params.limit);
    const raw = await api.get('enterprise/templates', { searchParams }).json();
    return raw as { templates: import('./types').EnterpriseTemplate[]; total: number; page: number; limit: number };
  },

  createEnterpriseTemplate: async (dto: import('./types').CreateEnterpriseTemplateDTO) => {
    const raw = await api.post('enterprise/templates', { json: dto }).json();
    return raw as import('./types').EnterpriseTemplate;
  },

  setDefaultEnterpriseTemplate: async (id: string) => {
    const raw = await api.post(`enterprise/templates/${id}/set-default`).json();
    return raw as import('./types').EnterpriseTemplate;
  },

  // ── Product (Workspace) Templates ──────────────────────────────────────

  listProductTemplates: async (params?: { tool_target?: string; active_only?: boolean; limit?: number }) => {
    const searchParams: Record<string, string> = {};
    if (params?.tool_target) searchParams.tool_target = params.tool_target;
    if (params?.active_only) searchParams.active_only = 'true';
    if (params?.limit)       searchParams.limit = String(params.limit);
    const raw = await api.get('product/templates', { searchParams }).json();
    return raw as { templates: import('./types').ProductTemplate[]; total: number; page: number; limit: number };
  },

  createProductTemplate: async (dto: import('./types').CreateProductTemplateDTO) => {
    const raw = await api.post('product/templates', { json: dto }).json();
    return raw as import('./types').ProductTemplate;
  },

  setDefaultProductTemplate: async (id: string) => {
    const raw = await api.post(`product/templates/${id}/set-default`).json();
    return raw as import('./types').ProductTemplate;
  },
};
